import { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useAttachLegacyRequisition } from "../../../hooks/useLegacyCases";

export default function LegacyRequisitionAttachment({
  archiveId,
}: {
  archiveId: string;
}) {
  const [opened, setOpened] = useState(false);
  const mutation = useAttachLegacyRequisition(archiveId);
  const form = useForm<{ s3Key: string; originalFileName: string }>({
    initialValues: { s3Key: "", originalFileName: "" },
    validateInputOnBlur: true,
    validate: {
      s3Key: (value) =>
        value.startsWith("novopath/requisitions/") &&
        value.length > "novopath/requisitions/".length
          ? null
          : "Enter a stored requisition path under novopath/requisitions/.",
      originalFileName: (value) =>
        /\.pdf$/i.test(value) ? null : "Enter the original PDF file name.",
    },
  });
  const handleOpen = useCallback(() => {
    mutation.reset();
    setOpened(true);
  }, [mutation]);
  const handleClose = useCallback(() => {
    if (!mutation.isPending) setOpened(false);
  }, [mutation.isPending]);
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const validation = form.validate();
      if (validation.hasErrors) {
        const node = form.getInputNode(Object.keys(validation.errors)[0]);
        node?.scrollIntoView({ block: "center" });
        node?.focus();
        return;
      }
      try {
        await mutation.mutateAsync(form.getValues());
        form.reset();
        setOpened(false);
      } catch {
        /* The translated mutation error is rendered in the modal. */
      }
    },
    [form, mutation]
  );
  return (
    <>
      <Button variant="default" onClick={handleOpen}>
        Attach stored requisition
      </Button>
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Attach a requisition"
        centered
        closeOnClickOutside={false}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            <Text size="sm">
              Attach a PDF already uploaded to the private requisition archive.
              It adds supporting evidence and leaves the final report and saved
              draft unchanged.
            </Text>
            <TextInput
              label="Stored requisition path"
              withAsterisk
              aria-required
              {...form.getInputProps("s3Key")}
            />
            <TextInput
              label="Original PDF file name"
              withAsterisk
              aria-required
              {...form.getInputProps("originalFileName")}
            />
            {mutation.error && (
              <Alert color="red" title="Requisition not attached">
                {mutation.error.message}
              </Alert>
            )}
            <Group justify="space-between">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                Attach requisition
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
